/**
 * Optimistic placement for pipeline board drags.
 *
 * Convex updates lag a frame behind dnd-kit's drop, so without a local
 * overlay the card snaps back to the source column until the query
 * refreshes. These helpers apply that overlay and know when the server
 * has caught up so it can be dropped.
 */

export interface PipelinePendingPlacement {
  jobId: string;
  columnKey: string;
  /** Index in the target column after the dragged job is removed. */
  insertIndex: number;
  /** Status/rank at drop time — used to detect the Convex refresh. */
  prevStatusId: string | null;
  prevRank: string | null;
}

export interface PlaceableJob {
  _id: string;
  preAppointmentStatusId?: string | null;
  preAppointmentBoardRank?: string | null;
}

export interface PlaceableColumn<T extends PlaceableJob> {
  key: string;
  jobs: ReadonlyArray<T>;
}

/**
 * Move `pending.jobId` into `pending.columnKey` at `insertIndex`. Other
 * column fields are preserved.
 */
export function applyPendingPlacement<
  T extends PlaceableJob,
  C extends PlaceableColumn<T>,
>(columns: ReadonlyArray<C>, pending: PipelinePendingPlacement | null): C[] {
  if (!pending) return [...columns];
  const job = columns
    .flatMap((c) => c.jobs)
    .find((j) => String(j._id) === pending.jobId);
  if (!job) return [...columns];

  return columns.map((col) => {
    const without = col.jobs.filter((j) => String(j._id) !== pending.jobId);
    if (col.key !== pending.columnKey) {
      return without.length === col.jobs.length
        ? col
        : { ...col, jobs: without };
    }
    const insertAt = Math.max(0, Math.min(pending.insertIndex, without.length));
    return {
      ...col,
      jobs: [...without.slice(0, insertAt), job, ...without.slice(insertAt)],
    };
  });
}

/**
 * True once the job's persisted status or rank differs from the snapshot
 * taken at drop — i.e. the list query has observed the mutation.
 */
export function serverCaughtUpToPlacement(
  jobs: ReadonlyArray<PlaceableJob>,
  pending: PipelinePendingPlacement,
): boolean {
  const job = jobs.find((j) => String(j._id) === pending.jobId);
  if (!job) return true;
  const statusId = job.preAppointmentStatusId
    ? String(job.preAppointmentStatusId)
    : null;
  const rank = job.preAppointmentBoardRank ?? null;
  return statusId !== pending.prevStatusId || rank !== pending.prevRank;
}
